/**
 * Parcours d'intégration — étapes du stepper, prochaine action du staff,
 * actions de statut proposées. Pur : aucune règle métier n'est tranchée ici
 * (la base valide chaque transition) ; on ne fait que décrire le parcours.
 */

export const STATUTS_LIVREUR = [
  'candidature', 'dossier_complet', 'entretien_fait', 'formation_faite', 'quiz_reussi', 'contrat_signe', 'actif',
] as const
export const STATUTS_MARCHAND = ['prospect', 'documents', 'contrat_envoye', 'contrat_signe', 'actif'] as const
export const STATUTS_HORS_PARCOURS = ['refuse', 'suspendu'] as const

export const LIBELLE_STATUT: Record<string, string> = {
  candidature: 'Candidature',
  dossier_complet: 'Dossier complet',
  entretien_fait: 'Entretien fait',
  formation_faite: 'Formation faite',
  quiz_reussi: 'Quiz réussi',
  contrat_signe: 'Contrat signé',
  actif: 'Actif',
  prospect: 'Prospect',
  documents: 'Pièces déposées',
  contrat_envoye: 'Contrat envoyé',
  refuse: 'Refusé',
  suspendu: 'Suspendu',
}

/** Couleur de pastille (classes `badge-*` de la feuille de style). */
export const COULEUR_STATUT: Record<string, string> = {
  candidature: 'gray',
  prospect: 'gray',
  dossier_complet: 'blue',
  documents: 'blue',
  entretien_fait: 'yellow',
  formation_faite: 'yellow',
  quiz_reussi: 'yellow',
  contrat_envoye: 'yellow',
  contrat_signe: 'orange',
  actif: 'green',
  refuse: 'red',
  suspendu: 'red',
}

const LIBELLE_ETAPE_LIVREUR: Record<string, string> = {
  candidature: 'Candidature',
  dossier_complet: 'Dossier',
  entretien_fait: 'Entretien',
  formation_faite: 'Formation',
  quiz_reussi: 'Quiz',
  contrat_signe: 'Contrat',
  actif: 'Actif',
}
const LIBELLE_ETAPE_MARCHAND: Record<string, string> = {
  prospect: 'Prospect',
  documents: 'Pièces',
  contrat_envoye: 'Contrat envoyé',
  contrat_signe: 'Contrat signé',
  actif: 'Actif',
}

/** `fait` · `en_cours` · `a_venir` · `arret` (là où un refus ou une suspension a interrompu le parcours). */
export type EtatEtape = 'fait' | 'en_cours' | 'a_venir' | 'arret'
export interface Etape {
  cle: string
  libelle: string
  etat: EtatEtape
}

function etapes(
  ordre: readonly string[],
  libelles: Record<string, string>,
  statut: string,
  avantArret: string | null | undefined,
  repliSuspendu: string,
): Etape[] {
  const construire = (fait: number, arret: boolean): Etape[] =>
    ordre.map((cle, i) => ({
      cle,
      libelle: libelles[cle] ?? cle,
      etat: arret
        ? i < fait ? 'fait' : i === fait ? 'arret' : 'a_venir'
        : i < fait ? 'fait' : i === fait ? 'en_cours' : 'a_venir',
    }))

  if (statut === 'refuse' || statut === 'suspendu') {
    /* Où le parcours s'est arrêté : l'étape d'avant le refus/la suspension
       (lue dans le journal) ; à défaut, le début pour un refus, l'étape finale
       pour une suspension (on ne suspend qu'un actif). */
    let k = avantArret ? ordre.indexOf(avantArret) : -1
    if (k < 0) k = statut === 'suspendu' ? ordre.indexOf(repliSuspendu) : 0
    return construire(k, true)
  }
  const idx = ordre.indexOf(statut)
  if (idx < 0) return ordre.map((cle) => ({ cle, libelle: libelles[cle] ?? cle, etat: 'a_venir' as const }))
  /* La dernière étape (actif) n'est pas « en cours » : le parcours est fini. */
  if (idx === ordre.length - 1) return ordre.map((cle) => ({ cle, libelle: libelles[cle] ?? cle, etat: 'fait' as const }))
  return construire(idx, false)
}

export function etapesLivreur(statut: string, avantArret?: string | null): Etape[] {
  return etapes(STATUTS_LIVREUR, LIBELLE_ETAPE_LIVREUR, statut, avantArret, 'actif')
}

export function etapesMarchand(statut: string, avantArret?: string | null): Etape[] {
  return etapes(STATUTS_MARCHAND, LIBELLE_ETAPE_MARCHAND, statut, avantArret, 'actif')
}

/** Le statut d'où l'on est parti vers refus/suspension, d'après le journal (le plus récent d'abord). */
export function statutAvantArret(
  journal: ReadonlyArray<{ de: string | null; vers: string }>,
): string | null {
  const e = journal.find((x) => x.vers === 'refuse' || x.vers === 'suspendu')
  return e?.de ?? null
}

// ── Prochaine action du staff ────────────────────────────────────────────

export interface ContexteDossier {
  piecesEnAttente?: number
  piecesManquantes?: number
  /** Livreur : 3 échecs au quiz, en attente de déblocage. */
  quizBloque?: boolean
  /** Un lien de signature électronique est déjà parti. */
  signatureEnCours?: boolean
  /** Commerçant : aucun compte de reversement saisi. */
  compteAbsent?: boolean
  /** Commerçant : compte saisi mais pas encore vérifié. */
  compteAVerifier?: boolean
  /** Nombre de champs d'identité juridique encore à compléter (0 = complète). Voir `manquantsDeLaSaisie`. */
  identiteManquante?: number
}

export interface ActionStaff {
  code: string
  libelle: string
  /** `true` : la balle est dans le camp du staff ; `false` : on attend le candidat. */
  pourStaff: boolean
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`

export function prochaineActionStaff(
  sujet: 'livreur' | 'commercant',
  statut: string,
  ctx: ContexteDossier = {},
): ActionStaff {
  const attente = ctx.piecesEnAttente ?? 0
  const manque = ctx.piecesManquantes ?? 0
  if (statut === 'actif') return { code: 'aucune', libelle: 'Rien à faire : dossier actif.', pourStaff: false }
  if (statut === 'refuse') {
    return { code: 'rouvrir', libelle: 'Dossier refusé : le rouvrir si la situation change.', pourStaff: false }
  }
  if (statut === 'suspendu') {
    return { code: 'lever_suspension', libelle: 'Suspendu : lever la suspension quand le motif est réglé.', pourStaff: true }
  }

  if (sujet === 'livreur') {
    switch (statut) {
      case 'candidature':
        if (attente > 0) return { code: 'valider_pieces', libelle: `Valider ou rejeter ${pluriel(attente, 'pièce')} déposée${attente > 1 ? 's' : ''}.`, pourStaff: true }
        return { code: 'attendre_pieces', libelle: manque > 0 ? `En attente du dépôt de ${pluriel(manque, 'pièce')} par le candidat.` : 'En attente des pièces du candidat.', pourStaff: false }
      case 'dossier_complet':
        return { code: 'entretien', libelle: "Mener l'entretien et l'enregistrer.", pourStaff: true }
      case 'entretien_fait':
        return { code: 'formation', libelle: 'Enregistrer la session de formation suivie.', pourStaff: true }
      case 'formation_faite':
        return ctx.quizBloque
          ? { code: 'debloquer_quiz', libelle: 'Quiz bloqué après 3 échecs : le débloquer.', pourStaff: true }
          : { code: 'attendre_quiz', libelle: 'En attente du quiz du candidat (dans son app).', pourStaff: false }
      case 'quiz_reussi':
        if (!ctx.signatureEnCours && (ctx.identiteManquante ?? 0) > 0) {
          return { code: 'completer_identite', libelle: "Identité juridique à compléter (par le livreur dans son app, ou ici) : sans elle, le contrat ne peut ni être signé ni être envoyé.", pourStaff: true }
        }
        return ctx.signatureEnCours
          ? { code: 'attendre_signature', libelle: 'Lien de signature envoyé : en attente de la signature.', pourStaff: false }
          : { code: 'contrat', libelle: 'Contrat : le candidat signe dans son app, ou envoyer un lien Documenso.', pourStaff: true }
      case 'contrat_signe':
        return { code: 'activer', libelle: 'Activer le livreur.', pourStaff: true }
    }
  } else {
    switch (statut) {
      case 'prospect':
        return { code: 'attendre_pieces', libelle: 'En attente du premier dépôt de pièces du gérant.', pourStaff: false }
      case 'documents':
        if (attente > 0) return { code: 'valider_pieces', libelle: `Valider ou rejeter ${pluriel(attente, 'pièce')} déposée${attente > 1 ? 's' : ''}.`, pourStaff: true }
        if (manque > 0) return { code: 'attendre_pieces', libelle: `En attente de ${pluriel(manque, 'pièce')} requise${manque > 1 ? 's' : ''}.`, pourStaff: false }
        if (ctx.compteAbsent) return { code: 'attendre_compte', libelle: 'En attente du compte de reversement du gérant.', pourStaff: false }
        if (ctx.compteAVerifier) return { code: 'verifier_compte', libelle: 'Vérifier le compte de reversement.', pourStaff: true }
        if ((ctx.identiteManquante ?? 0) > 0) return { code: 'completer_identite', libelle: "Compléter l'identité juridique (statut et numéros) : elle désigne le commerçant dans le contrat.", pourStaff: true }
        return { code: 'envoyer_contrat', libelle: 'Dossier prêt : envoyer le contrat.', pourStaff: true }
      case 'contrat_envoye':
        return { code: 'attendre_signature', libelle: 'Contrat envoyé : en attente de la signature du gérant.', pourStaff: false }
      case 'contrat_signe':
        return { code: 'activer', libelle: 'Activer le commerçant.', pourStaff: true }
    }
  }
  return { code: 'aucune', libelle: '—', pourStaff: false }
}

// ── Actions de statut proposées ─────────────────────────────────────────

export interface ActionStatut {
  vers: string
  libelle: string
  motifRequis: boolean
  ton: 'principal' | 'danger' | 'neutre'
}

/**
 * Les décisions que le staff prend à la main (UPDATE du statut). Les passages
 * automatiques (dossier complet, entretien, formation, quiz, signature)
 * n'apparaissent pas : les déclencheurs de la base s'en chargent. La base
 * valide quand même chaque transition et ses préconditions.
 */
export function actionsStatut(sujet: 'livreur' | 'commercant', statut: string): ActionStatut[] {
  const refuser: ActionStatut = { vers: 'refuse', libelle: 'Refuser', motifRequis: true, ton: 'danger' }
  const actions: ActionStatut[] = []
  if (sujet === 'commercant' && statut === 'documents') {
    actions.push({ vers: 'contrat_envoye', libelle: 'Envoyer le contrat', motifRequis: false, ton: 'principal' })
  }
  if (statut === 'contrat_signe') {
    actions.push({
      vers: 'actif',
      libelle: sujet === 'livreur' ? 'Activer le livreur' : 'Activer le commerçant',
      motifRequis: false,
      ton: 'principal',
    })
  }
  const ordre: readonly string[] = sujet === 'livreur' ? STATUTS_LIVREUR : STATUTS_MARCHAND
  const i = ordre.indexOf(statut)
  if (i >= 0 && i < ordre.length - 1) actions.push(refuser)
  if (statut === 'actif') actions.push({ vers: 'suspendu', libelle: 'Suspendre', motifRequis: true, ton: 'danger' })
  if (statut === 'refuse') {
    actions.push({
      vers: sujet === 'livreur' ? 'candidature' : 'prospect',
      libelle: 'Rouvrir le dossier',
      motifRequis: false,
      ton: 'neutre',
    })
  }
  if (statut === 'suspendu') {
    actions.push({ vers: 'actif', libelle: 'Lever la suspension', motifRequis: false, ton: 'principal' })
  }
  return actions
}
